import React, { useState } from 'react';
import { LocalFolder } from '../types/project';
import {
    FolderIcon,
    FolderOpenIcon,
    PlusIcon,
    Trash2Icon
} from './icons';

interface FolderTreeProps {
    folders: LocalFolder[];
    selectedFolderId: string | null;
    onSelectFolder: (folderId: string | null) => void;
    onCreateFolder: (name: string, parentId?: string | null) => void;
    onDeleteFolder: (folderId: string) => void;
    onDropItem?: (itemId: string, targetFolderId: string | null) => void;
    acceptDropType?: string; // e.g., 'character', 'dogma'
    className?: string;
}

export const FolderTree: React.FC<FolderTreeProps> = ({
    folders,
    selectedFolderId,
    onSelectFolder,
    onCreateFolder,
    onDeleteFolder,
    onDropItem,
    acceptDropType = 'item',
    className = ''
}) => {
    const [isCreating, setIsCreating] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [dragOverFolderId, setDragOverFolderId] = useState<string | null | 'root'>(null);

    const handleDragOver = (e: React.DragEvent, folderId: string | null) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types.includes(`application/x-${acceptDropType}`)) {
            e.dataTransfer.dropEffect = 'move';
            setDragOverFolderId(folderId === null ? 'root' : folderId);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOverFolderId(null);
    };

    const handleDrop = (e: React.DragEvent, targetFolderId: string | null) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverFolderId(null);

        const itemId = e.dataTransfer.getData(`application/x-${acceptDropType}`);
        if (itemId && onDropItem) {
            onDropItem(itemId, targetFolderId);
        }
    };

    const handleCreateSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (newFolderName.trim()) {
            onCreateFolder(newFolderName.trim());
            setNewFolderName('');
            setIsCreating(false);
        }
    };

    const sortedFolders = [...folders].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div className={`flex flex-col h-full bg-gray-900/50 ${className}`}>
            {/* Header / Main Actions */}
            <div className="p-3 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                    Dossiers
                </h3>
                <button
                    onClick={() => setIsCreating(true)}
                    className="p-1 hover:bg-gray-700 rounded-md text-gray-400 hover:text-white transition-colors"
                    title="Nouveau Dossier"
                >
                    <PlusIcon className="w-4 h-4" />
                </button>
            </div>

            {/* Folder List */}
            <div className="flex-grow overflow-y-auto p-2 space-y-1">

                {/* "All Characters" Root Item */}
                <button
                    onClick={() => onSelectFolder(null)}
                    onDragOver={(e) => handleDragOver(e, null)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, null)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg text-sm font-medium transition-all ${
                        dragOverFolderId === 'root'
                            ? 'bg-indigo-500/30 border-2 border-indigo-400 border-dashed'
                            : selectedFolderId === null
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                >
                    <FolderOpenIcon className="w-4 h-4" />
                    <span>Tous les personnages</span>
                </button>

                {/* Dynamic Folders */}
                {sortedFolders.map(folder => (
                    <div
                        key={folder.id}
                        className="group relative"
                        onDragOver={(e) => handleDragOver(e, folder.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, folder.id)}
                    >
                        <button
                            onClick={() => onSelectFolder(folder.id)}
                            className={`w-full flex items-center gap-3 p-2 rounded-lg text-sm font-medium transition-all ${
                                dragOverFolderId === folder.id
                                    ? 'bg-indigo-500/30 border-2 border-indigo-400 border-dashed'
                                    : selectedFolderId === folder.id
                                    ? 'bg-indigo-600/50 text-indigo-100 border border-indigo-500/30'
                                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                            }`}
                        >
                            <FolderIcon className={`w-4 h-4 ${
                                dragOverFolderId === folder.id ? 'text-indigo-400' :
                                selectedFolderId === folder.id ? 'text-indigo-300' : 'text-gray-500'
                            }`} />
                            <span className="truncate">{folder.name}</span>
                        </button>

                        {/* Delete Action (only visible on hover or if selected) */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (confirm(`Supprimer le dossier "${folder.name}" ?`)) {
                                    onDeleteFolder(folder.id);
                                }
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Supprimer"
                        >
                            <Trash2Icon className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ))}

                {/* Creation Input */}
                {isCreating && (
                    <form onSubmit={handleCreateSubmit} className="p-2 bg-gray-800 rounded-lg border border-indigo-500/50">
                        <input
                            autoFocus
                            type="text"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            placeholder="Nom du dossier..."
                            className="w-full bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none mb-2"
                            onBlur={() => {
                                if (!newFolderName.trim()) setIsCreating(false);
                            }}
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()} // Prevent blur
                                onClick={() => setIsCreating(false)}
                                className="text-xs text-gray-400 hover:text-white"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                onMouseDown={(e) => e.preventDefault()} // Prevent blur
                                className="text-xs text-indigo-400 hover:text-indigo-300 font-semibold"
                            >
                                Créer
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};
